/* AdminUnderwriting — Buyer Underwriting Hub (Feb 2026)
 *
 * One-screen oversight for everything underwriting-related per buyer:
 *
 *   • Provider scorecards (Middesk · Plaid · Two.inc · Allianz)
 *   • Live verqo Trade Score with 10-signal breakdown
 *   • Credit memos history (AI underwriter outputs)
 *   • Auto-grant + adverse-action timeline
 *   • Manual override controls (re-run · approve · deny · cap · hold)
 *
 * Closes the 4 admin-visibility gaps user flagged:
 *   1. Surface kyb_records + credit_memos + two_credit + allianz + score
 *   2. Plaid relink visibility (backed by webhook auto-action)
 *   3. Single unified timeline
 *   4. Two.inc result no longer black-box
 */
import { useEffect, useMemo, useState } from "react";
import {
  Search, Loader2, Building2, Banknote, ShieldCheck, Sparkles,
  FileText, Activity, AlertTriangle, RefreshCw, CheckCircle2, XCircle,
  Clock, ArrowRight, ChevronDown, ChevronUp, Gauge, History, BadgeCheck,
  Hash, TrendingUp, TrendingDown, FileSearch, AlertCircle, Lock, Info,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  SCORE_SIGNAL_LABELS,
  SCORE_SIGNAL_FALLBACK_WEIGHTS_PCT,
  SCORE_WEIGHTED_KEYS,
  SCORE_TIER_EXPLAINERS,
  SCORE_DISCLAIMER,
  normalizeRating,
} from "../../lib/scoreLabels";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../../components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../../components/ui/dialog";


function fmtUSD(n) {
  if (n == null) return "—";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}


// ─── Provider scorecard ────────────────────────────────────────────────
const PROVIDER_META = {
  middesk:  { label: "Middesk · KYB",       icon: Building2,  color: "violet" },
  plaid:    { label: "Plaid · Banking",     icon: Banknote,   color: "sky" },
  two_inc:  { label: "Two.inc · Credit",    icon: BadgeCheck, color: "emerald" },
  allianz:  { label: "Allianz · Insurance", icon: ShieldCheck, color: "amber" },
};

const STATUS_TONE = {
  approved:      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  active:        "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  connected:     "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  pending:       "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  open:          "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  declined:      "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  error:         "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  not_submitted: "bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-400",
  not_connected: "bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-400",
  not_checked:   "bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-400",
  none:          "bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-400",
  needs_relink:  "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
};


function ProviderCard({ name, data }) {
  const meta = PROVIDER_META[name];
  const Icon = meta?.icon || FileText;
  const tone = STATUS_TONE[data.status] || STATUS_TONE.not_submitted;
  const summary = data.summary || {};

  return (
    <div className="rounded-xl border bg-white dark:bg-slate-950 p-4"
         data-testid={`underwriting-provider-${name}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center bg-${meta?.color || "slate"}-100 text-${meta?.color || "slate"}-600`}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              {meta?.label || name}
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider mt-0.5 inline-block ${tone}`}>
              {data.status}
            </span>
          </div>
        </div>
        {data.ok ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        ) : (
          <XCircle className="h-4 w-4 text-slate-300" />
        )}
      </div>

      {/* Per-provider key facts */}
      <div className="mt-3 space-y-1 text-[11px] text-slate-600 dark:text-slate-400">
        {name === "middesk" && summary.submitted && (
          <>
            <div>TIN match: <b>{String(summary.tin_match)}</b></div>
            {summary.ofac_hit !== undefined && (
              <div>OFAC: <b>{summary.ofac_hit ? "hit" : "clean"}</b></div>
            )}
            {summary.business_age_years != null && (
              <div>Age: <b>{summary.business_age_years}y</b></div>
            )}
            {summary.error && <div className="text-rose-600 text-[10px]">Err: {summary.error.slice(0, 60)}</div>}
          </>
        )}
        {name === "plaid" && summary.connected && (
          <>
            <div>{summary.institution} · {summary.accounts_count} accts · {summary.ach_capable} ACH</div>
            {summary.months_of_data != null && <div>{summary.months_of_data}mo data · NSF {summary.nsf_count ?? 0}</div>}
            {summary.needs_relink && (
              <div className="text-rose-600 text-[10px] flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Relink needed: {summary.relink_reason}
              </div>
            )}
          </>
        )}
        {name === "two_inc" && summary.checked && (
          <>
            <div>Country: <b>{summary.country}</b></div>
            {summary.requested_amount && <div>Requested: <b>{fmtUSD(summary.requested_amount)}</b></div>}
            {summary.decline_reason && <div className="text-rose-600 text-[10px]">Reason: {summary.decline_reason}</div>}
          </>
        )}
        {name === "allianz" && (
          <>
            <div>Active covers: <b>{summary.active_covers || 0}</b></div>
            {summary.active_cover_usd > 0 && <div>Cover total: <b>{fmtUSD(summary.active_cover_usd)}</b></div>}
            {summary.total_covers > 0 && <div className="text-[10px] text-slate-500">{summary.total_covers} lifetime covers</div>}
          </>
        )}
        {(!summary.submitted && !summary.connected && !summary.checked && !(summary.active_covers > 0)) && (
          <div className="text-slate-400 italic">Not started</div>
        )}
      </div>
    </div>
  );
}


// ─── Score breakdown panel ─────────────────────────────────────────────
function ScorePanel({ score }) {
  if (!score) {
    return (
      <div className="rounded-xl border bg-white dark:bg-slate-950 p-5 text-sm text-slate-500"
           data-testid="underwriting-score-panel">
        Score not available.
      </div>
    );
  }
  const signals = score.signals || {};
  const breakdown = score.breakdown || {};
  const caps = score.caps_applied || [];
  const bonuses = score.bonuses_applied || [];

  const ratingLabel = normalizeRating(score.rating);
  const ratingExplainer = score.tier_explainer || SCORE_TIER_EXPLAINERS[ratingLabel] || "";
  const disclaimer = score.disclaimer || SCORE_DISCLAIMER;

  const band =
    score.score >= 780 ? "emerald" :
    score.score >= 700 ? "sky" :
    score.score >= 640 ? "amber" :
    "rose";

  return (
    <div className="rounded-xl border bg-white dark:bg-slate-950 p-5"
         data-testid="underwriting-score-panel">
      {/* v5.2 · Neutral disclaimer — verqo is a data provider, not a lender */}
      <div
        className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 text-amber-900 dark:text-amber-200 px-3 py-2 text-[11.5px] leading-relaxed flex items-start gap-2"
        data-testid="underwriting-score-disclaimer"
        role="note"
      >
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"/>
        <div>
          <span className="font-semibold">Informational only.</span>{" "}
          {disclaimer}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Gauge className="h-4 w-4 text-slate-500" />
          Live verqo Trade Score
        </h3>
        <Badge variant="outline" className="text-[10px]">live-computed</Badge>
      </div>

      <div className="flex items-end gap-4">
        <div>
          <div className={`text-5xl font-bold tabular-nums text-${band}-600`}
               data-testid="underwriting-score-value">
            {score.score}
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-xs text-slate-500 uppercase tracking-wider inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                data-testid="underwriting-score-tier"
                aria-label={`Tier ${ratingLabel || ""} — explanation`}
              >
                {ratingLabel}
                <Info className="w-3 h-3 opacity-70"/>
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              className="max-w-xs text-[12.5px] leading-relaxed"
              data-testid="underwriting-score-tier-popover"
            >
              <div className="font-medium mb-1">{ratingLabel}</div>
              <div className="text-slate-600 dark:text-slate-400">{ratingExplainer}</div>
              <div className="text-[11px] text-slate-400 mt-2">
                Description of observed data — not a credit recommendation.
              </div>
            </PopoverContent>
          </Popover>
          {score.percentile != null && (
            <div
              className="mt-1 text-[10px] mono text-slate-500"
              data-testid="underwriting-score-percentile"
              title={`Percentile vs ${score.cohort_size || 0} peer buyers on verqo`}
            >
              Top {Math.max(1, 100 - score.percentile)}% of peers
            </div>
          )}
        </div>
        <div className="flex-1 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg border p-2">
            <div className="text-[10px] text-slate-500 uppercase">Tier-band ref. limit</div>
            <div className="font-semibold tabular-nums" data-testid="underwriting-score-limit">
              {fmtUSD(score.credit_limit)}
            </div>
          </div>
          <div className="rounded-lg border p-2">
            <div className="text-[10px] text-slate-500 uppercase">Reference rate</div>
            <div className="font-semibold tabular-nums">
              {((score.rate || 0) * 100).toFixed(2)}%
            </div>
          </div>
        </div>
      </div>

      {/* Signal bars · v5.2 · only the 10 weighted buckets */}
      <div className="mt-5 space-y-1.5" data-testid="underwriting-signals">
        <div className="text-[10px] mono uppercase tracking-widest text-slate-500 mb-1.5 flex items-center justify-between">
          <span>10 weighted buckets · v5.2</span>
          {score.weights && (
            <span className="text-slate-400">
              Σ {Math.round(Object.values(score.weights).reduce((a, b) => a + b, 0) * 100)}%
            </span>
          )}
        </div>
        {SCORE_WEIGHTED_KEYS.filter(k => signals[k] !== undefined).map(k => {
          const v = signals[k];
          const pct = Math.max(0, Math.min(100, Number(v) || 0));
          const wPct = score.weights?.[k] != null
            ? Math.round(score.weights[k] * 100)
            : (SCORE_SIGNAL_FALLBACK_WEIGHTS_PCT[k] ?? 0);
          const barTone =
            pct >= 75 ? "bg-emerald-500" :
            pct >= 50 ? "bg-sky-500" :
            pct >= 25 ? "bg-amber-500" : "bg-rose-500";
          return (
            <div key={k} className="flex items-center gap-2 text-xs"
                 data-testid={`signal-${k}`}>
              <div className="w-44 truncate text-slate-600 dark:text-slate-400">
                {SCORE_SIGNAL_LABELS[k] || k.replace(/_/g, " ")}
                <span className="text-slate-400 ml-1">· {wPct}%</span>
              </div>
              <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-900 overflow-hidden">
                <div className={`h-full ${barTone}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="w-10 text-right tabular-nums font-medium">{pct}</div>
            </div>
          );
        })}
      </div>

      {/* v5.2 · Stage-1 Eligibility Gate */}
      {score.eligibility_gate && (
        <div className="mt-4 rounded-lg border bg-slate-50 dark:bg-slate-900 p-3"
             data-testid="underwriting-eligibility-gate">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] mono uppercase tracking-widest text-slate-500">
              Stage-1 Eligibility Gate
            </div>
            <span className={`text-[9px] mono px-1.5 py-0.5 rounded-full ${
              score.eligibility_gate.eligible
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-amber-50 text-amber-800 border border-amber-200"
            }`}>
              {score.eligibility_gate.eligible ? "PASSING" : "GATE FAIL"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1.5 text-[11px]">
            {(score.eligibility_gate.gates || []).map(g => (
              <div key={g.key} className="flex items-center gap-1.5">
                <span className={`mono text-[10px] ${
                  g.ok ? "text-emerald-700" : (g.kind === "hard" ? "text-rose-700" : "text-amber-700")
                }`}>{g.ok ? "✓" : "✗"}</span>
                <span className="text-slate-600 dark:text-slate-400 capitalize">
                  {g.key.replace(/_/g, " ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* v5.2 · Guardrails applied */}
      {score.guardrails_applied?.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
             data-testid="underwriting-guardrails">
          <div className="text-[10px] mono uppercase tracking-widest text-amber-700 mb-1.5">
            Guardrails applied
          </div>
          <ul className="text-[11.5px] text-amber-900 space-y-1">
            {score.guardrails_applied.map((g, i) => <li key={i}>• {g}</li>)}
          </ul>
        </div>
      )}

      {/* Caps + bonuses */}
      {(caps.length > 0 || bonuses.length > 0) && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {caps.map((c, i) => (
            <Badge key={`cap-${i}`} variant="destructive" className="text-[10px]">
              CAP: {typeof c === "string" ? c : (c.label || JSON.stringify(c))}
            </Badge>
          ))}
          {bonuses.map((b, i) => (
            <Badge key={`b-${i}`} className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
              + {typeof b === "string" ? b : (b.label || JSON.stringify(b))}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Decision dialog ───────────────────────────────────────────────────
function DecisionDialog({ open, onClose, buyer, onApplied }) {
  const [decision, setDecision] = useState("approve");
  const [limit, setLimit] = useState("");
  const [terms, setTerms] = useState("net30");
  const [reason, setReason] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open) {
      setDecision("approve");
      setLimit(""); setTerms("net30"); setReason("");
    }
  }, [open]);

  const apply = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason ≥ 3 chars required");
      return;
    }
    if ((decision === "approve" || decision === "cap") && !limit) {
      toast.error(`limit_usd required for ${decision}`);
      return;
    }
    setApplying(true);
    try {
      const payload = { decision, reason: reason.trim() };
      if (decision === "approve" || decision === "cap") {
        payload.limit_usd = Number(limit);
      }
      if (decision === "approve") payload.terms = terms;
      const { data } = await api.post(
        `/admin/underwriting/${buyer.id}/decision`, payload,
      );
      toast.success(`Decision applied · ${decision} · override ${data.override_id.slice(0, 8)}`);
      onApplied && onApplied(data);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Decision failed"));
    } finally {
      setApplying(false);
    }
  };

  if (!buyer) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="underwriting-decision-dialog">
        <DialogHeader>
          <DialogTitle>Apply Underwriting Decision</DialogTitle>
          <DialogDescription>
            Override the AI recommendation for <b>{buyer.email}</b>. All decisions are
            hash-chained + the buyer is notified.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium block mb-1">Decision</label>
            <div className="grid grid-cols-4 gap-1.5">
              {["approve", "deny", "cap", "hold"].map((d) => (
                <button
                  key={d}
                  onClick={() => setDecision(d)}
                  data-testid={`decision-${d}`}
                  className={`text-xs px-3 py-2 rounded-md border capitalize transition ${
                    decision === d
                      ? "bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900"
                      : "hover:bg-slate-50 dark:hover:bg-slate-900"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {(decision === "approve" || decision === "cap") && (
            <div>
              <label className="text-xs font-medium block mb-1">
                Credit limit (USD) <span className="text-rose-500">*</span>
              </label>
              <Input
                type="number" min={0}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                data-testid="decision-limit"
              />
            </div>
          )}

          {decision === "approve" && (
            <div>
              <label className="text-xs font-medium block mb-1">Terms</label>
              <select
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                data-testid="decision-terms"
                className="w-full text-sm border rounded px-2 py-2 bg-white dark:bg-slate-950"
              >
                <option value="prepaid">Prepaid</option>
                <option value="wallet_only">Wallet only</option>
                <option value="net7">Net 7</option>
                <option value="net14">Net 14</option>
                <option value="net30">Net 30</option>
                <option value="net60">Net 60</option>
                <option value="net90">Net 90</option>
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-medium block mb-1">
              Reason <span className="text-rose-500">*</span>
            </label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="≥3 chars · hash-chained + shown to buyer"
              data-testid="decision-reason"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} disabled={applying} data-testid="decision-apply">
            {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Apply decision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Timeline panel ────────────────────────────────────────────────────
const KIND_META = {
  kyb_submitted:       { icon: Building2, tone: "violet", label: "KYB" },
  plaid_connected:     { icon: Banknote, tone: "sky", label: "Plaid" },
  two_check:           { icon: BadgeCheck, tone: "emerald", label: "Two.inc" },
  allianz_grant:       { icon: ShieldCheck, tone: "amber", label: "Allianz" },
  memo_generated:      { icon: Sparkles, tone: "indigo", label: "AI memo" },
  auto_grant:          { icon: TrendingUp, tone: "emerald", label: "Auto-grant" },
  credit_app_decision: { icon: FileText, tone: "slate", label: "Credit app" },
  adverse_notice:      { icon: AlertTriangle, tone: "rose", label: "Adverse" },
  admin_override:      { icon: Lock, tone: "amber", label: "Override" },
};

function TimelineRow({ ev }) {
  const m = KIND_META[ev.kind] || { icon: Activity, tone: "slate", label: ev.kind };
  const Icon = m.icon;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b last:border-0"
         data-testid={`timeline-row-${ev.kind}`}>
      <div className={`shrink-0 mt-0.5 h-7 w-7 rounded-full flex items-center justify-center bg-${m.tone}-100 text-${m.tone}-600`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-[10px]">{m.label}</Badge>
          <div className="text-sm font-medium truncate">{ev.summary}</div>
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {fmtTime(ev.at)} · {ev.actor?.slice(0, 30) || "—"}
        </div>
      </div>
    </div>
  );
}


// ─── Main page ─────────────────────────────────────────────────────────
export default function AdminUnderwriting() {
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [buyer, setBuyer] = useState(null);
  const [hub, setHub] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [rerunReason, setRerunReason] = useState("");
  const [rerunning, setRerunning] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const doSearch = async () => {
    if (q.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/support/search`, {
        params: { q: q.trim(), kind: "user", limit: 15 },
      });
      const buyers = (data.rows || []).filter((u) => u.role === "buyer");
      setSearchResults(buyers);
    } catch (e) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const loadBuyer = async (b) => {
    setBuyer(b);
    setLoading(true);
    setHub(null); setTimeline([]);
    try {
      const [h, t] = await Promise.all([
        api.get(`/admin/underwriting/${b.id}`),
        api.get(`/admin/underwriting/${b.id}/timeline`, { params: { limit: 50 } }),
      ]);
      setHub(h.data);
      setTimeline(t.data.rows || []);
    } catch (e) {
      toast.error("Load failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const reloadHub = async () => {
    if (!buyer) return;
    setLoading(true);
    try {
      const [h, t] = await Promise.all([
        api.get(`/admin/underwriting/${buyer.id}`),
        api.get(`/admin/underwriting/${buyer.id}/timeline`, { params: { limit: 50 } }),
      ]);
      setHub(h.data);
      setTimeline(t.data.rows || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const rerun = async () => {
    if (rerunReason.trim().length < 3) {
      toast.error("Reason ≥ 3 chars required");
      return;
    }
    setRerunning(true);
    try {
      const { data } = await api.post(
        `/admin/underwriting/${buyer.id}/rerun`,
        { reason: rerunReason.trim() },
      );
      toast.success(`AI memo generated · ${data.memo.recommended_decision} · ${fmtUSD(data.memo.recommended_credit_limit_usd)}`);
      setRerunOpen(false);
      setRerunReason("");
      await reloadHub();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Re-run failed"));
    } finally {
      setRerunning(false);
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="admin-underwriting">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <FileSearch className="h-5 w-5 text-slate-700 dark:text-slate-300" />
          <h1 className="text-2xl font-bold">Underwriting Hub</h1>
        </div>
        <p className="text-sm text-slate-500">
          One-screen oversight: KYB · Banking · Credit check · Insurance · AI memo · Live score · Manual overrides.
        </p>
      </div>

      {/* Buyer search */}
      <div className="rounded-2xl border bg-white dark:bg-slate-950 p-4" data-testid="underwriting-search">
        <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 block">
          Find buyer
        </label>
        <div className="flex gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            placeholder="Email, name, ID, or company…"
            data-testid="underwriting-search-input"
          />
          <Button onClick={doSearch} disabled={searching || q.trim().length < 2}
                  data-testid="underwriting-search-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        {searchResults.length > 0 && (
          <div className="mt-2 space-y-1" data-testid="underwriting-search-results">
            {searchResults.map((b) => (
              <button
                key={b.id}
                onClick={() => loadBuyer(b)}
                data-testid={`underwriting-pick-${b.id}`}
                className={`w-full flex items-center justify-between gap-2 rounded-lg border p-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-900 transition ${
                  buyer?.id === b.id ? "bg-slate-100 dark:bg-slate-900" : ""
                }`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{b.email}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {b.name} · {b.company} · {b.id.slice(0, 8)}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading underwriting hub…
        </div>
      )}

      {!loading && hub && (
        <>
          {/* Buyer header + actions */}
          <div className="rounded-2xl border bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 p-5"
               data-testid="underwriting-buyer-header">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-lg font-bold">{hub.buyer.email}</div>
                <div className="text-xs text-slate-500">
                  {hub.buyer.name} · {hub.buyer.company} ·
                  KYC: <b>{hub.buyer.kyc_status || "—"}</b> · ID: {hub.buyer.id.slice(0, 12)}
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <Badge variant="outline" className="text-[11px]">
                    Provider completion: <b className="ml-1">{hub.completion_pct}%</b>
                  </Badge>
                  {hub.ai_grant?.active && (
                    <Badge className="text-[11px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                      AI grant active · {fmtUSD(hub.ai_grant.limit_usd)}
                    </Badge>
                  )}
                  {hub.admin_limit_override != null && (
                    <Badge className="text-[11px] bg-amber-100 text-amber-700 hover:bg-amber-100">
                      Admin cap: {fmtUSD(hub.admin_limit_override)}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={reloadHub}
                        data-testid="underwriting-refresh">
                  <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={() => setRerunOpen(true)}
                        data-testid="underwriting-rerun-btn">
                  <Sparkles className="h-4 w-4 mr-1" /> Re-run AI
                </Button>
                <Button size="sm" onClick={() => setDecisionOpen(true)}
                        data-testid="underwriting-decision-btn">
                  <Hash className="h-4 w-4 mr-1" /> Apply decision
                </Button>
              </div>
            </div>
          </div>

          {/* Provider cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
               data-testid="underwriting-providers-grid">
            {Object.entries(hub.providers).map(([name, data]) => (
              <ProviderCard key={name} name={name} data={data} />
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1.5 border-b" data-testid="underwriting-tabs">
            {[
              { id: "overview", label: "Score & Signals" },
              { id: "memos",    label: `AI Memos (${hub.memos.length})` },
              { id: "timeline", label: `Timeline (${timeline.length})` },
              { id: "compliance", label: `Adverse notices (${hub.adverse_notices.length})` },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`uw-tab-${t.id}`}
                className={`text-sm px-3 py-2 border-b-2 transition ${
                  activeTab === t.id
                    ? "border-slate-900 dark:border-slate-100 font-semibold"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === "overview" && <ScorePanel score={hub.score} />}

          {activeTab === "memos" && (
            <div className="rounded-2xl border bg-white dark:bg-slate-950 p-5"
                 data-testid="underwriting-memos">
              {hub.memos.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-8">No AI memos yet.</div>
              ) : (
                <div className="space-y-2">
                  {hub.memos.map((m, i) => (
                    <div key={m.id} className="rounded-lg border p-3"
                         data-testid={`memo-row-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {m.recommended_decision}
                          </Badge>
                          <span className="text-xs text-slate-500">{m.source}</span>
                        </div>
                        <span className="text-[11px] text-slate-500">{fmtTime(m.generated_at)}</span>
                      </div>
                      <div className="text-sm">
                        Limit: <b className="tabular-nums">{fmtUSD(m.recommended_credit_limit_usd)}</b>
                        {" · "}Terms: <b>{m.recommended_terms}</b>
                        {" · "}Confidence: <b>{((m.confidence || 0) * 100).toFixed(0)}%</b>
                      </div>
                      {(m.key_strengths || []).length > 0 && (
                        <div className="text-[11px] text-emerald-700 mt-1">
                          ✓ {m.key_strengths.slice(0, 2).join(" · ")}
                        </div>
                      )}
                      {(m.key_risks || []).length > 0 && (
                        <div className="text-[11px] text-rose-700 mt-1">
                          ⚠ {m.key_risks.slice(0, 2).join(" · ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "timeline" && (
            <div className="rounded-2xl border bg-white dark:bg-slate-950 p-5"
                 data-testid="underwriting-timeline">
              {timeline.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-8">No events.</div>
              ) : (
                <div>
                  {timeline.map((ev, i) => <TimelineRow key={i} ev={ev} />)}
                </div>
              )}
            </div>
          )}

          {activeTab === "compliance" && (
            <div className="rounded-2xl border bg-white dark:bg-slate-950 p-5"
                 data-testid="underwriting-compliance">
              {hub.adverse_notices.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-8">
                  No adverse-action notices for this buyer.
                </div>
              ) : (
                <div className="space-y-2">
                  {hub.adverse_notices.map((n) => (
                    <div key={n.id} className="rounded-lg border p-3"
                         data-testid={`adverse-row-${n.id}`}>
                      <div className="flex items-center justify-between">
                        <Badge variant="destructive" className="text-[10px]">
                          {n.kind}
                        </Badge>
                        <span className="text-[11px] text-slate-500">{fmtTime(n.issued_at)}</span>
                      </div>
                      <div className="text-xs mt-1.5">
                        Email sent: <b>{String(n.email_sent)}</b>
                        {n.previous_limit_usd != null && (
                          <span> · Limit drop: {fmtUSD(n.previous_limit_usd)} → {fmtUSD(n.new_limit_usd)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!loading && !hub && !buyer && (
        <div className="rounded-2xl border bg-white dark:bg-slate-950 p-12 text-center text-slate-500"
             data-testid="underwriting-empty">
          <FileSearch className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <div className="text-sm">Search a buyer above to load their underwriting profile.</div>
        </div>
      )}

      {/* Re-run dialog */}
      <Dialog open={rerunOpen} onOpenChange={setRerunOpen}>
        <DialogContent data-testid="underwriting-rerun-dialog">
          <DialogHeader>
            <DialogTitle>Re-run AI underwriter</DialogTitle>
            <DialogDescription>
              Pulls fresh KYB + Plaid data and runs Verqo AI. Persists a new memo + may trigger auto-grant.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={rerunReason}
            onChange={(e) => setRerunReason(e.target.value)}
            placeholder="Audit reason ≥3 chars (e.g. fresh Plaid data, customer dispute)"
            data-testid="rerun-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRerunOpen(false)}>Cancel</Button>
            <Button onClick={rerun} disabled={rerunning} data-testid="rerun-submit">
              {rerunning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
              Run underwriter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DecisionDialog
        open={decisionOpen}
        onClose={() => setDecisionOpen(false)}
        buyer={buyer}
        onApplied={() => reloadHub()}
      />
    </div>
  );
}
